import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Users,
  Plus,
  Search,
  Loader2,
  Lock,
  Globe,
  Leaf
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Groups = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newGroup, setNewGroup] = useState({
    name: '',
    description: '',
    category: 'general',
    image_url: '',
    is_private: false
  });

  const categories = [
    { value: 'all', label: 'All Groups' },
    { value: 'general', label: 'General' },
    { value: 'succulents', label: 'Succulents' },
    { value: 'tropicals', label: 'Tropicals' },
    { value: 'cacti', label: 'Cacti' },
    { value: 'orchids', label: 'Orchids' },
    { value: 'herbs', label: 'Herbs & Edibles' },
    { value: 'outdoor', label: 'Outdoor Gardens' },
    { value: 'rare', label: 'Rare Plants' },
    { value: 'local', label: 'Local Communities' }
  ];

  useEffect(() => {
    fetchGroups();
  }, [categoryFilter]);

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const url = categoryFilter === 'all' 
        ? `${API}/groups` 
        : `${API}/groups?category=${categoryFilter}`;
      const response = await axios.get(url);
      setGroups(response.data);
    } catch (error) {
      console.error('Error fetching groups:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroup.name.trim() || !newGroup.description.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }
    setCreating(true);
    try {
      const response = await axios.post(`${API}/groups`, newGroup);
      setGroups([response.data, ...groups]);
      setNewGroup({
        name: '',
        description: '',
        category: 'general',
        image_url: '',
        is_private: false
      });
      setCreateOpen(false);
      toast.success('Group created!');
    } catch (error) {
      toast.error('Failed to create group');
    } finally {
      setCreating(false);
    }
  };

  const handleJoinGroup = async (groupId, e) => {
    e.stopPropagation();
    try {
      await axios.post(`${API}/groups/${groupId}/join`);
      setGroups(groups.map(g => 
        g.id === groupId ? { ...g, members_count: g.members_count + 1 } : g
      ));
      toast.success('Joined group!');
    } catch (error) {
      if (error.response?.data?.detail === 'Already a member') {
        toast.info('You\'re already a member');
      } else {
        toast.error('Failed to join group');
      }
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      'general': 'bg-muted text-muted-foreground',
      'succulents': 'bg-green-100 text-green-700',
      'tropicals': 'bg-emerald-100 text-emerald-700',
      'cacti': 'bg-lime-100 text-lime-700',
      'orchids': 'bg-pink-100 text-pink-700',
      'herbs': 'bg-amber-100 text-amber-700',
      'outdoor': 'bg-sky-100 text-sky-700',
      'rare': 'bg-purple-100 text-purple-700',
      'local': 'bg-orange-100 text-orange-700'
    };
    return colors[category] || 'bg-muted text-muted-foreground';
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-4xl font-heading font-medium mb-2">Groups</h1>
            <p className="text-muted-foreground text-lg">
              Find your tribe and connect with specialized pet communities
            </p>
          </div>

          {user && (
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button className="btn-primary" data-testid="create-group-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Group
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-heading">Create a Group</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Group Name *</Label>
                    <Input
                      placeholder="e.g., NYC Plant Lovers"
                      value={newGroup.name}
                      onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
                      data-testid="group-name-input"
                      className="input-field"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Description *</Label>
                    <Textarea
                      placeholder="What's this group about?"
                      value={newGroup.description}
                      onChange={(e) => setNewGroup({ ...newGroup, description: e.target.value })}
                      rows={3}
                      data-testid="group-description-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select
                      value={newGroup.category}
                      onValueChange={(val) => setNewGroup({ ...newGroup, category: val })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.slice(1).map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Cover Image URL</Label>
                    <Input
                      placeholder="https://..."
                      value={newGroup.image_url}
                      onChange={(e) => setNewGroup({ ...newGroup, image_url: e.target.value })}
                      className="input-field"
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <div className="flex items-center gap-2">
                      <Lock className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm">Private Group</span>
                    </div>
                    <Switch
                      checked={newGroup.is_private}
                      onCheckedChange={(checked) => setNewGroup({ ...newGroup, is_private: checked })}
                    />
                  </div>

                  <Button 
                    className="w-full btn-primary"
                    onClick={handleCreateGroup}
                    disabled={creating}
                    data-testid="submit-group-btn"
                  >
                    {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Create Group
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search groups..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 input-field"
              data-testid="group-search"
            />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-full sm:w-48" data-testid="group-category-filter">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              {categories.map(cat => (
                <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Groups Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : groups.length === 0 ? (
          <Card className="text-center py-16">
            <CardContent>
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
                <Users className="w-10 h-10 text-muted-foreground" />
              </div>
              <h3 className="font-heading text-xl mb-2">No groups found</h3>
              <p className="text-muted-foreground mb-4">
                {searchQuery || categoryFilter !== 'all' ? 'Try different filters' : 'Be the first to create a group!'}
              </p>
              {user && (
                <Button className="btn-primary" onClick={() => setCreateOpen(true)}>
                  Create First Group
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {groups.filter(group => 
              group.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
              group.description.toLowerCase().includes(searchQuery.toLowerCase())
            ).map((group) => (
              <Card 
                key={group.id}
                className="card-interactive overflow-hidden"
                data-testid={`group-card-${group.id}`}
              >
                <div className="aspect-video bg-muted relative overflow-hidden">
                  {group.image_url ? (
                    <img 
                      src={group.image_url}
                      alt={group.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/20 to-accent/20">
                      <Leaf className="w-12 h-12 text-primary/40" />
                    </div>
                  )}
                  <div className="group-card-overlay" />
                  <div className="absolute bottom-3 left-3 right-3">
                    <h3 className="font-heading text-lg font-medium text-white mb-1">{group.name}</h3>
                    <div className="flex items-center gap-2">
                      <Badge className={getCategoryColor(group.category)}>
                        {group.category}
                      </Badge>
                      {group.is_private ? (
                        <Badge variant="secondary" className="bg-white/20 text-white">
                          <Lock className="w-3 h-3 mr-1" />
                          Private
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="bg-white/20 text-white">
                          <Globe className="w-3 h-3 mr-1" />
                          Public
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                
                <CardContent className="p-4">
                  <p className="text-muted-foreground text-sm line-clamp-2 mb-4">{group.description}</p>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Users className="w-4 h-4" />
                      <span>{group.members_count} members</span>
                    </div>
                    
                    {user && (
                      <Button 
                        variant="outline"
                        size="sm"
                        onClick={(e) => handleJoinGroup(group.id, e)}
                        data-testid={`join-group-${group.id}`}
                      >
                        Join
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Groups;
